import React, { useEffect, useState } from 'react';

declare global {
    interface Window {
        initBlueskyComments: (elementId: string, uri: string) => void;
    }
}

interface BlueskyCommentsProps {
    uri: string;
}

const BlueskyComments: React.FC<BlueskyCommentsProps> = ({ uri }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadScript = (src: string): Promise<void> => {
            return new Promise((resolve, reject) => {
                // Check if script is already loaded
                if (document.querySelector(`script[src="${src}"]`)) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = src;
                script.async = true;
                script.onload = () => {
                    // Wait a bit to ensure the script is fully initialized
                    setTimeout(resolve, 100);
                };
                script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
                document.body.appendChild(script);
            });
        };

        const initComments = () => {
            // Wait for the function to be available
            const checkInitFunction = () => {
                if (window.initBlueskyComments) {
                    try {
                        // Convert bsky.app URL to at:// URI format if needed
                        const formattedUri = uri.includes('bsky.app') 
                            ? uri.split('/post/')[1] 
                            : uri;
                        
                        window.initBlueskyComments('bluesky-comments', formattedUri);
                        setIsLoaded(true);
                    } catch (err) {
                        setError('Failed to initialize Bluesky comments');
                        console.error('Failed to initialize Bluesky comments:', err);
                    }
                } else {
                    // If function is not available yet, try again after a short delay
                    setTimeout(checkInitFunction, 100);
                }
            };

            checkInitFunction();
        };

        const loadScriptsAndInit = async () => {
            try {
                // Load the Bluesky comments script with specific version
                await loadScript('https://unpkg.com/bluesky-comments@0.1.0/dist/bluesky-comments.umd.js');
                initComments();
            } catch (error) {
                setError('Failed to load Bluesky comments');
                console.error('Failed to load scripts:', error);
            }
        };

        loadScriptsAndInit();

        return () => {
            // Cleanup if necessary
            const script = document.querySelector('script[src*="bluesky-comments"]');
            if (script) {
                script.remove();
            }
        };
    }, [uri]);

    return (
        <div>
            <div id="bluesky-comments"></div>
            {!isLoaded && !error && <p>Loading Bluesky comments...</p>}
            {error && <p className="text-danger">{error}</p>}
        </div>
    );
};

export default BlueskyComments;